/**
 * "There are bookings at your other restaurant."
 *
 * The admin app shows one venue at a time, which is right for a host standing
 * at a stand. The cost is that a booking arriving at the venue you are NOT
 * looking at is completely invisible — no badge, no count, nothing.
 *
 * That is not a theoretical gap. The first widget bookings on this system went
 * to The Pearl while the screen showed West Bay, and the honest reading from
 * the other side of the screen was "reservations from the website are not
 * reaching the admin". They were; they were three metres to the left.
 *
 * So: when the venue on screen is quiet for this date and another one is not,
 * say so, and offer the switch. It renders nothing at all in the ordinary case,
 * which is most of the time.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useVenues } from '../hooks/useVenue.js';

interface VenueCount {
  venue_id: string;
  total: number;
}

export function OtherVenueNotice({ date }: { date: string }) {
  const { t, i18n } = useTranslation();
  const { venueId, setVenue } = useSession();
  const { data: venues } = useVenues();

  const { data } = useQuery({
    queryKey: ['venue-counts', date],
    queryFn: () => api.get<{ counts: VenueCount[] }>('/api/v1/venues/counts', { date }),
    enabled: Boolean(date),
  });

  const counts = data?.counts ?? [];
  // Only worth saying when there is more than one venue to be confused between.
  if (counts.length < 2) return null;

  const here = counts.find((c) => c.venue_id === venueId)?.total ?? 0;
  const elsewhere = counts.filter((c) => c.venue_id !== venueId && c.total > 0);

  // Silent unless the current venue is empty and another is not. A manager with
  // a full book at both venues does not need telling.
  if (here > 0 || elsewhere.length === 0) return null;

  return (
    <div className="rounded-lg border border-brand-bronze bg-brand-bronze-light px-4 py-3 text-sm">
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {elsewhere.map((c) => {
          const venue = venues?.find((v) => v.id === c.venue_id);
          const name = !venue
            ? c.venue_id
            : i18n.language === 'ar'
              ? venue.name_ar
              : venue.name_en;
          return (
            <li key={c.venue_id}>
              <span className="text-brand-bronze-dark">
                {t('venueNotice.elsewhere', { count: c.total, venue: name })}
              </span>{' '}
              <button
                type="button"
                onClick={() => setVenue(c.venue_id)}
                className="font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal-dark"
              >
                {t('venueNotice.switch')}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
